/**
 * usePayment - Composable principal pour la gestion des paiements
 *
 * Ce composable fournit une interface unifiée pour interagir avec
 * différents providers de paiement (Stripe, PayPal, etc.)
 */

import { ref, computed } from 'vue';
import { createPaymentProviders } from '../config/payment-config';

export function usePayment(amount, currency, metadata = {}) {
  // Créer les instances de providers
  const providers = createPaymentProviders();

  // Provider sélectionné par défaut (le premier de la liste)
  const selectedProviderId = ref(providers[0]?.id || null);

  // États de traitement
  const processing = ref(false);
  const error = ref(null);

  /**
   * Retourne le provider sélectionné
   */
  const selectedProvider = computed(() => {
    return providers.find(p => p.id === selectedProviderId.value);
  });

  /**
   * Retourne uniquement les providers actifs
   */
  const enabledProviders = computed(() => {
    return providers.filter(p => p.isEnabled());
  });

  /**
   * Initialise le provider sélectionné
   * Charge les SDK, configure les tokens, etc.
   * @returns {Promise<any>} Le provider initialisé
   */
  async function initializeProvider() {
    if (!selectedProvider.value) {
      throw new Error('No payment provider selected');
    }

    return await selectedProvider.value.initialize();
  }

  /**
   * Crée un paiement auprès du provider
   * Génère un PaymentIntent (Stripe), Order ID (PayPal), etc.
   * @returns {Promise<object>} Données du paiement créé
   */
  async function createPayment() {
    if (!selectedProvider.value) {
      throw new Error('No payment provider selected');
    }

    // Formater le montant selon le provider
    const formattedAmount = selectedProvider.value.formatAmount(amount.value, currency.value);

    return await selectedProvider.value.createPayment(
      formattedAmount,
      currency.value,
      metadata
    );
  }

  /**
   * Traite le paiement
   * Confirme la carte (Stripe), capture l'ordre (PayPal), etc.
   * @param {object} paymentData - Données spécifiques au provider
   * @returns {Promise<object>} Résultat du paiement
   */
  async function processPayment(paymentData) {
    if (!selectedProvider.value) {
      throw new Error('No payment provider selected');
    }

    processing.value = true;
    error.value = null;

    try {
      const result = await selectedProvider.value.processPayment(paymentData);
      return result;
    } catch (e) {
      error.value = e.message || 'Payment failed';
      throw e;
    } finally {
      processing.value = false;
    }
  }

  /**
   * Change le provider sélectionné
   * @param {string} providerId - ID du nouveau provider
   */
  function selectProvider(providerId) {
    const provider = providers.find(p => p.id === providerId);
    if (provider && provider.isEnabled()) {
      selectedProviderId.value = providerId;
    } else {
      throw new Error(`Provider ${providerId} not found or disabled`);
    }
  }

  return {
    // Providers disponibles
    providers,
    enabledProviders,

    // Provider sélectionné
    selectedProviderId,
    selectedProvider,

    // États
    processing,
    error,

    // Méthodes
    initializeProvider,
    createPayment,
    processPayment,
    selectProvider
  };
}
